import { useEffect, useState } from "react";
import { QuietField } from "../../components/QuietField";
import { api } from "../../api/client";
import type { UserPick } from "../../api/types";

// 專案承辦人(也就是專案負責同仁)的下拉。
//
// 業主原話:「報價單中要寫這個專案中 是公司的哪位同仁負責的」。
// 甲方那一塊只有這一格是可以動的 —— 選了誰,承辦人電話與 EMAIL 就跟著他走,
// 不另外開三個手填欄位,否則同一位同仁會在不同單子上有不同的電話。
//
// 清單來自 GET /api/users/pickable,只回啟用中的帳號與姓名/Email/電話三欄。
// 那支端點刻意不是老闆專屬:企劃自己開單時就要選得到人。

/** 沒有指派負責同仁時的選項。報價單常常是先開單、之後再派人。 */
const UNASSIGNED = "";

export interface OwnerPickerProps {
  /** 目前的負責同仁 id;還沒派人就是 null。 */
  value: number | null;
  /**
   * 這張單上目前顯示的承辦人姓名(後端推導的 `party_a.contact_name`)。
   *
   * 清單載不到、或那位同仁已經被停用(停用的人不會出現在可選清單裡)時,
   * 靠它把名字留在畫面上。少了這一手,下拉會顯示成「未指派」,
   * 使用者只是存個檔就把別人負責的單子悄悄改成沒有人負責。
   */
  currentName: string;
  /** 選了誰。選「未指派」時給 null。 */
  onChange: (user: UserPick | null) => void;
}

export function OwnerPicker({ value, currentName, onChange }: OwnerPickerProps) {
  const [users, setUsers] = useState<UserPick[]>([]);

  useEffect(() => {
    let alive = true;
    api
      .get<UserPick[]>("/users/pickable")
      .then((rows) => {
        // 測試替身或半路壞掉的回應可能不是陣列,先擋住再進畫面。
        if (alive && Array.isArray(rows)) setUsers(rows);
      })
      .catch(() => {
        // 清單載不到不該讓整張單編不下去:下拉維持現值,其餘欄位照常編輯。
      });
    return () => {
      alive = false;
    };
  }, []);

  const options = [{ value: UNASSIGNED, label: "未指派" }, ...users.map((user) => ({ value: String(user.id), label: user.name }))];
  // 現在掛著的那位不在可選清單裡(已停用,或清單還沒載回來):
  // 補一個選項把他留在畫面上,不讓下拉自己跳成「未指派」。
  if (value !== null && !users.some((user) => user.id === value)) {
    options.splice(1, 0, { value: String(value), label: currentName === "" ? `同仁 #${value}` : currentName });
  }

  const pick = (raw: string) => {
    if (raw === UNASSIGNED) {
      onChange(null);
      return;
    }
    const picked = users.find((user) => String(user.id) === raw);
    // 找不到就是選到上面補的那一個 —— 它本來就是現值,不必也不該動。
    if (picked) onChange(picked);
  };

  return (
    <QuietField
      as="select"
      label="專案承辦人"
      options={options}
      value={value === null ? UNASSIGNED : String(value)}
      onChange={(event) => pick(event.target.value)}
    />
  );
}
